/* eslint-disable react/jsx-props-no-spreading */
import { FormikProps } from "formik";
import React, { FunctionComponent, useEffect, useMemo } from "react";
import { DataField } from "@iventis/domain-model/model/dataField";
import { VenueLocation } from "@iventis/domain-model/model/venueLocation";
import { media, styled } from "@iventis/styles";
import { Content } from "@iventis/translations/content/typed-content";
import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { Header3 } from "@iventis/styles/src/components/texts";
import { AutocompleteWithLoading, StyledHorizontalRule } from "@iventis/components";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { formFieldVerticalGap, formPadding, timeInputMixin } from "@iventis/styles/src/atomic-rules";
import { EventBlock } from "@iventis/domain-model/model/eventBlock";
import { DataFieldValue } from "@iventis/domain-model/model/dataFieldValue";
import { inputCheckboxCSS } from "@iventis/styles/src/components/checkbox-elements";
import { Hierarchy } from "@iventis/domain-model/model/hierarchy";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { titleFormat } from "@iventis/utilities";

import { SubEventValues } from "./sub-event.types";
import { StyledSectionHorizontalInner } from "./session-form";
import { findHierarchyEntityById } from "./hierarchy.helpers";
import { getPotentialRelativeEventBlocks } from "./sub-event-relative.helpers";
import { TimeDelegatorError, TimeTypeDelegator } from "./time-type-delegator";
import { getTimeTypeDelegatorInputPropsFromEventBlockType } from "./time-rules.helpers";
import { EventDataFieldInputs } from "./event-data-field-inputs";
import { hasEventBlockUpdated, mapSubEventFormValuesToEventBlock, subSessionDefaultColour } from "./event-block-helpers";

interface SubEventFormProps extends FormikProps<SubEventValues> {
    setCanSubmit: (canSubmit: boolean) => void;
    parentEvent: Hierarchy<EventBlock>;
    eventBlockType: ScheduleConfigEventBlockType;
    dataFields: DataField[];
    venueHierarchy: Hierarchy<VenueLocation>[];
    eventBlockTypes: ScheduleConfigEventBlockType[];
    scheduleDaysMap: Map<number, Date>;
    currentDate: Date;
    existingEvent: EventBlock;
    session: EventBlock;
    disciplineCodeDataFieldValue: DataFieldValue;
    timezone: string;
}

// Used to represent multiple inputs as one
const customFormikErrors = ["relativeEventRules"];

export const SubEventForm: FunctionComponent<SubEventFormProps> = ({
    setCanSubmit,
    parentEvent,
    eventBlockType,
    dataFields,
    venueHierarchy,
    disciplineCodeDataFieldValue,
    eventBlockTypes,
    scheduleDaysMap,
    currentDate,
    existingEvent,
    session,
    timezone,
    ...formikProps
}) => {
    const { isValid, handleSubmit, handleBlur, setFieldValue, setStatus, setFieldError, status, touched, errors, values } = formikProps;

    // Update form container to enable/disable save buttons
    useEffect(() => {
        let canSubmit = existingEvent == null;
        if (existingEvent != null) {
            const latestEventBlock = mapSubEventFormValuesToEventBlock(values, existingEvent, session.startAt, timezone, dataFields);
            const hasUpdated = hasEventBlockUpdated(existingEvent, latestEventBlock);
            if (hasUpdated) {
                canSubmit = true;
            }
        }
        setCanSubmit(isValid && canSubmit);
    }, [isValid, values]);

    const translate = useIventisTranslate();

    useEffect(() => {
        // Loop through all custom formik errors
        customFormikErrors.forEach((errorKey) => {
            // if error is present in status then set on error object
            const error = status?.[errorKey];
            if (error != null) {
                setFieldError(errorKey, error);
            }
        });
    }, [errors, status]);

    // Venues
    const availableVenues: VenueLocation[] = useMemo(
        () =>
            findHierarchyEntityById(venueHierarchy, parentEvent.item.venueLocation.id)
                .children.map((v) => v.item)
                .sort((a, b) => a.name.localeCompare(b.name)),
        []
    );

    const potentialEventBlocksToRelateTo = useMemo(() => getPotentialRelativeEventBlocks(parentEvent, existingEvent?.id), [parentEvent]);

    return (
        <StyledForm onSubmit={handleSubmit}>
            <Header3>{translate(Content.schedule2.data_fields.event_name)}</Header3>

            <EventDataFieldInputs
                eventBlockTypeDataFields={eventBlockType.dataFields}
                allDataFields={dataFields}
                values={values}
                additionalNonEventBlockDataFieldValues={[disciplineCodeDataFieldValue]}
                setFieldValue={setFieldValue}
            />

            <div className="hr-container">
                <StyledHorizontalRule />
            </div>

            <Header3>{translate(Content.schedule.scheduled_item.time)}</Header3>
            <StyledSectionHorizontalInner className="single-column-selectors">
                <TimeTypeDelegator
                    values={values}
                    setFieldValue={setFieldValue}
                    potentialEventBlocksToRelateTo={potentialEventBlocksToRelateTo}
                    currentDate={currentDate}
                    eventBlockTypes={eventBlockTypes}
                    currentEventBlockType={eventBlockType}
                    scheduleDaysMap={scheduleDaysMap}
                    className="time-type-delegator"
                    errors={errors as TimeDelegatorError}
                    handleError={(val, valid) => {
                        if (valid && status != null) {
                            delete status[val];
                            setStatus({ ...status });
                        } else if (!valid) {
                            setStatus({ ...status, [val]: "Error" });
                        }
                    }}
                    {...getTimeTypeDelegatorInputPropsFromEventBlockType(eventBlockType)}
                    timezone={timezone}
                />
            </StyledSectionHorizontalInner>

            <div className="hr-container">
                <StyledHorizontalRule />
            </div>

            <Header3>{translate(Content.schedule.scheduled_item.location)}</Header3>
            <div className="single-column-selectors">
                <StyledFieldLabel className="venue-label">{translate(Content.schedule.scheduled_item.space)}</StyledFieldLabel>
                <AutocompleteWithLoading
                    className="venue-input"
                    value={values.venueLocation || null}
                    placeholder={translate(Content.common.buttons.select)}
                    options={availableVenues}
                    onChange={(value) => setFieldValue("venueLocation", value)}
                    getOptionLabel={(option: VenueLocation) => (option ? titleFormat(option?.name) : "")}
                    getOptionSelected={(option: VenueLocation, value: VenueLocation) => option?.id === value?.id}
                    onBlur={handleBlur}
                    error={touched.venueLocation && errors.venueLocation !== undefined}
                    helperText={touched.venueLocation && errors.venueLocation?.toString()}
                    loadingOptions={false}
                />
            </div>
        </StyledForm>
    );
};

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    padding: ${formPadding};
    background-color: ${subSessionDefaultColour.fill};
    border-radius: 4px;
    border: 2px solid ${subSessionDefaultColour.outline};
    gap: ${formFieldVerticalGap};
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    .MuiInputBase-root {
        background-color: white;
    }
    .MuiAutocomplete-inputRoot {
        padding-top: 3px;
        padding-bottom: 3px;
    }
    .MuiOutlinedInput-notchedOutline {
        border-color: rgba(0, 0, 0, 0.7);
        border-width: 1.5px;
    }

    .inner-inner-box {
        display: flex;
        align-items: flex-start;
        grid-row: 2 / 3;
    }

    .auto-complete {
        max-height: 100px;
    }

    .hr-container {
        width: 100%;
        margin: 10px 0px;
    }

    .minutes-padding {
        flex: 0 0 158px;
        ${timeInputMixin}
    }

    .after-text {
        text-align: left;
        ${media.medium} {
            text-align: center;
        }
    }
    .item-padding {
        padding-bottom: 15px;
        ${media.medium} {
            padding-bottom: 0px;
        }
    }

    .single-column-selectors {
        display: flex;
        flex-direction: column;

        .venue-input {
            width: 100%;
            ${media.extraLarge} {
                width: 50%;
            }
        }

        .time-container {
            display: flex;
            justify-content: center;
            align-items: space-between;
            flex-direction: column;
            justify-content: stretch;

            ${media.medium} {
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
                margin-bottom: 15px;
            }
        }

        .min-time-width {
            min-width: 240px;
        }

        .duration-textfield {
            width: 100%;
        }

        .checkbox-container {
            display: grid;

            .checkbox-input {
                ${inputCheckboxCSS}
            }
        }
    }

    .time-type-delegator {
        display: flex;
        flex-direction: column;
        gap: ${formFieldVerticalGap};
        .top-time-type-row {
            margin-top: 15px;
        }
        .time-selector-group {
            display: grid;
            grid-template-columns: 50% 50%;
            gap: 0 10px;

            ${media.extraSmall} {
                grid-template-columns: 100%;
                gap: ${formFieldVerticalGap};
            }

            .duration {
                ${timeInputMixin}
            }
        }
    }
`;
